import { useState, type FormEvent, type ReactNode } from 'react';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@london-life/ui/components/dialog';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';

type Props = {
  open: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  canSubmit?: boolean;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  children: ReactNode;
};

// create and edit forms open here: fields, then Save; a failure stays beside the form with its message
export function FormDialog({
  open,
  title,
  description,
  submitLabel,
  canSubmit = true,
  onSubmit,
  onClose,
  children,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      await onSubmit();
      onClose();
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)} className="admin-form-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          {failure && <ErrorNotice error={failure} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !canSubmit}>
              {busy ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
